import { flatten } from 'lodash';
import { useMemo } from 'react';
import useReceiptLineItemErrors, {
  ReceiptLineItemError,
  ReceiptLineItemInfo,
  ReceiptLineItemWarning,
} from 'src/sections/receipt_details/errors/useReceiptLineItemErrors';
import { GLCodeDropdownData } from 'src/services/useGlCodesApi';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';
import { IReceipt } from 'src/types/Receipt';
import useReceiptDetailsInfoErrors from './useReceiptDetailsInfoErrors';

export interface ReceiptDetailsErrors {
  lineItems: ReceiptLineItemError[];
  documentDate: string[];
  deliveryDateTime: string[];
  deliveryDriverName: string[];
  deliveryCompanyName: string[];
  notes: string[];
}

export interface ReceiptDetailsInfo {
  lineItems: ReceiptLineItemInfo[];
}

export interface ReceiptDetailsWarnings {
  lineItems: ReceiptLineItemWarning[];
}

const useReceiptDetailsErrors = (
  lineItems: ReceiptLineItem[],
  receipt: IReceipt,
  glOptions: GLCodeDropdownData
) => {
  const {
    errors: lineItemErrors,
    infos: lineItemInfos,
    warnings: lineItemWarnings,
  } = useReceiptLineItemErrors(lineItems, glOptions);

  const {
    documentDateErrors,
    deliveryDateTimeErrors,
    deliveryDriverNameErrors,
    deliveryCompanyNameErrors,
    noteErrors,
  } = useReceiptDetailsInfoErrors(receipt);

  const errors: ReceiptDetailsErrors = useMemo(
    () => ({
      lineItems: lineItemErrors,
      documentDate: documentDateErrors,
      deliveryDateTime: deliveryDateTimeErrors,
      deliveryDriverName: deliveryDriverNameErrors,
      deliveryCompanyName: deliveryCompanyNameErrors,
      notes: noteErrors,
    }),
    [
      deliveryCompanyNameErrors,
      deliveryDateTimeErrors,
      deliveryDriverNameErrors,
      documentDateErrors,
      lineItemErrors,
      noteErrors,
    ]
  );
  const infos: ReceiptDetailsInfo = useMemo(
    () => ({
      lineItems: lineItemInfos,
    }),
    [lineItemInfos]
  );

  const warnings: ReceiptDetailsWarnings = useMemo(
    () => ({
      lineItems: lineItemWarnings,
    }),
    [lineItemWarnings]
  );

  const errorList: string[] = flatten(
    Object.values([
      ...Object.values(errors)
        .map((i) => Object.values(i)?.flat())
        .flat()
        .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
        .flat()
        .filter((item: any, i, arr) => arr.indexOf(item) === i),
    ] as string[])
  );

  const warningList: string[] = flatten(
    Object.values([
      ...Object.values(warnings)
        .map((i) => Object.values(i)?.flat())
        .flat()
        .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
        .flat()
        .filter((item: any, i, arr) => arr.indexOf(item) === i),
    ] as string[])
  );

  const infosList: string[] = flatten(
    Object.values([
      ...Object.values(infos)
        .map((i) => Object.values(i)?.flat())
        .flat()
        .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
        .flat()
        .filter((item: any, i, arr) => arr.indexOf(item) === i),
    ] as string[])
  );

  const hasErrors = useMemo(() => !!errorList?.length, [errorList.length]);
  const hasWarnings = useMemo(() => !!warningList?.length, [warningList.length]);
  const hasinfos = useMemo(() => !!infosList.length || 0, [infosList.length]);

  return {
    errors,
    errorList,
    infos,
    infosList,
    lineItemErrors,
    lineItemInfos,
    hasErrors,
    hasinfos,
    warningList,
    hasWarnings,
    warnings,
  };
};

export default useReceiptDetailsErrors;
